/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import React, { MouseEventHandler, CSSProperties, forwardRef, useCallback } from "react";

import { useCosmosComponentContext } from "@components/context";
import { CosmosLinkType } from "@components/context/DefaultLink";
import { AnalyticEventProps } from "@components/types";
import { useEmitEventOnClick } from "@components/utils/analytics";
import { safeToStringUrl, isRelative, isSameOrigin, pathnameHasLocale, isLauncher } from "@cosmos/common-utils";
import { useLocale, useLocales } from "@epic-mw/i18n";

export interface CosmosLinkProps extends CosmosLinkType {
	className?: string;
	style?: CSSProperties;
	children?: React.ReactNode;
	analytics?: AnalyticEventProps;
	disabled?: boolean;
	title?: string;
	target?: string;
	// TODO: discuss this error;
	href: string | CosmosLinkType["href"];
}

interface CosmosLinkButtonProps extends Omit<CosmosLinkProps, "onClick" | "href"> {
	onClick?: MouseEventHandler<HTMLButtonElement> | undefined;
}

const CosmosLinkButton = ({
	children,
	analytics,
	className,
	style,
	disabled,
	onClick: originalOnClick,
	title,
}: CosmosLinkButtonProps): React.JSX.Element => {
	const emitEvent = useEmitEventOnClick(analytics);
	const onClick: MouseEventHandler<HTMLButtonElement> = useCallback(
		(e) => {
			emitEvent(e);

			if (originalOnClick && typeof originalOnClick === "function") {
				originalOnClick(e);
			}
		},
		[originalOnClick, emitEvent],
	);

	return (
		<button className={className} style={style} onClick={onClick} disabled={disabled} title={title}>
			{children}
		</button>
	);
};

export const CosmosLink = forwardRef(
	(
		{ children, analytics, disabled, ...props }: CosmosLinkProps,
		forwardedRef: React.Ref<HTMLAnchorElement>,
	): React.JSX.Element => {
		const { Link } = useCosmosComponentContext();
		const emitEvent = useEmitEventOnClick(analytics);
		const locale = useLocale();
		const locales = useLocales();

		const originalOnClick = props.onClick as MouseEventHandler<HTMLAnchorElement | HTMLButtonElement>;

		const onClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
			(e) => {
				emitEvent(e);

				if (originalOnClick && typeof originalOnClick === "function") {
					originalOnClick(e);
				}
			},
			[originalOnClick, emitEvent],
		);

		if (!props.href) {
			return (
				<CosmosLinkButton
					analytics={analytics}
					onClick={originalOnClick}
					className={props.className}
					style={props.style}
					disabled={disabled}
					title={props?.title}
					target={props?.target}
				>
					{children}
				</CosmosLinkButton>
			);
		}

		// Handle launcher URLs: treat as Epic (same-origin), use href as-is, target="_self"
		const hrefString = typeof props.href === "string" ? props.href : safeToStringUrl(props.href);
		if (isLauncher(hrefString)) {
			const finalTarget = props.target || "_self";
			const rel = finalTarget === "_blank" ? "noopener noreferrer" : "";
			const finalHref = props.href; // Use href as-is without modifications

			return (
				<Link {...props} href={finalHref} target={finalTarget || ""} rel={rel} onClick={onClick} ref={forwardedRef}>
					{children}
				</Link>
			);
		}

		const isEpic = isSameOrigin(hrefString);
		const finalTarget = props.target || isEpic ? "_self" : "_blank";
		const rel = finalTarget === "_blank" ? "noopener noreferrer" : "";

		//try to inject locale when href is a string
		let finalHref: string | CosmosLinkType["href"] = props.href;
		if (typeof finalHref === "string") {
			finalHref = finalHref.replace(":locale", locale).replace("{locale}", locale);

			if (isRelative(finalHref) && !pathnameHasLocale(locales, finalHref)) {
				finalHref = `/${locale}${finalHref}`;
			}
		}

		return (
			<Link {...props} href={finalHref} target={finalTarget || ""} rel={rel} onClick={onClick} ref={forwardedRef}>
				{children}
			</Link>
		);
	},
);

CosmosLink.displayName = "CosmosLink";
